import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Drawer, Switch, TextField, Autocomplete, Chip, Checkbox, FormControlLabel, Button, Divider, IconButton, CircularProgress } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import ReactQuill from 'react-quill-new';
import "quill/dist/quill.snow.css";
import toast from 'react-hot-toast';
import { fetchPayerEmailConfiguration, updatePayerEmailConfiguration } from '../../../services/api/index';

// Deliberately simple, as on the Email Configuration page: one @, a dot in the domain, no spaces.
const EMAIL_PATTERN = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

const editorModules = {
  toolbar: [
    [{ header: '1' }, { header: '2' }, { font: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['bold', 'italic', 'underline'],
    ['link'],
  ],
};

const editorFormats = ['header', 'font', 'bold', 'italic', 'underline', 'list', 'link'];

// Filled in by the API when the roster is approved (PayerEmailPlaceholders).
const PLACEHOLDERS = ['{Payer Name}', '{Cycle Name}', '{Admin Name}', '{Organization Name}'];

// Starting text for a payer whose email has no subject or message yet.
const DEFAULT_SUBJECT = 'Provider Roster Submission – {Payer Name} – {Cycle}';
const DEFAULT_BODY =
  '<p>Dear {Payer Name},</p><p><br></p>' +
  '<p>The provider roster for {Payer Name} - {Cycle Name} Cycle has been generated and reviewed by our team and is being submitted to you for further action.</p><p><br></p>' +
  '<p>Please find the attached updated file and let us know if any additional information or corrections are needed.</p><p><br></p>' +
  '<p>Thank you,</p><p>{Admin Name}</p><p>{Organization Name}</p>';

const emptyConfig = {
  sendEmail: false,
  fromAddress: '',
  toAddresses: [],
  ccAddresses: [],
  subject: '',
  body: '',
  includeRosterAttachment: false,
};

const fromApi = (data) => {
  const isNew = !data?.subject && !data?.body;
  return {
    sendEmail: !!data?.send_email,
    fromAddress: data?.from_address || '',
    toAddresses: data?.to_addresses || [],
    ccAddresses: data?.cc_addresses || [],
    subject: isNew ? DEFAULT_SUBJECT : (data?.subject || ''),
    body: isNew ? DEFAULT_BODY : (data?.body || ''),
    includeRosterAttachment: !!data?.include_roster_attachment,
  };
};

const toApi = (config) => ({
  send_email: config.sendEmail,
  to_addresses: config.toAddresses,
  cc_addresses: config.ccAddresses,
  subject: config.subject.trim(),
  body: config.body,
  include_roster_attachment: config.includeRosterAttachment,
});

function validate(config) {
  const errors = [];
  const invalid = [...config.toAddresses, ...config.ccAddresses].filter((a) => !EMAIL_PATTERN.test(a));
  if (invalid.length) errors.push(`Not a valid email address: ${invalid.join(', ')}`);
  if (config.sendEmail) {
    if (!config.toAddresses.length) errors.push('At least one recipient is required');
    if (!config.subject.trim()) errors.push('Subject is required');
  }
  return errors;
}

const labelSx = { width: 64, flexShrink: 0, fontSize: 13, color: '#344049' };
const rowSx = { display: 'flex', alignItems: 'center', py: 1.5 };

/** Chip input for addresses: Enter, comma, space or leaving the field turns the typed text into a chip. */
function EmailChipsField({ value, onChange, placeholder }) {
  const [input, setInput] = useState('');

  const commit = (text) => {
    const parts = text.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const seen = new Set(value.map((v) => v.toLowerCase()));
    const added = parts.filter((p) => !seen.has(p.toLowerCase()) && seen.add(p.toLowerCase()));
    if (added.length) onChange([...value, ...added]);
    setInput('');
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      fullWidth
      options={[]}
      value={value}
      inputValue={input}
      onInputChange={(e, text, reason) => {
        if (reason !== 'input') return;
        if (/[,;\s]/.test(text)) commit(text);
        else setInput(text);
      }}
      onChange={(e, newValue) => {
        setInput('');
        onChange(newValue.map((v) => v.trim()).filter(Boolean));
      }}
      renderTags={(tags, getTagProps) =>
        tags.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option}
            label={option}
            size="small"
            sx={{
              fontSize: 12,
              backgroundColor: EMAIL_PATTERN.test(option) ? '#eef0f2' : '#fdecea',
              color: EMAIL_PATTERN.test(option) ? '#344049' : '#c62828',
            }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder={value.length ? '' : placeholder}
          onBlur={() => commit(input)}
          InputProps={{ ...params.InputProps, disableUnderline: true, sx: { fontSize: 13 } }}
        />
      )}
    />
  );
}

const PayerEmailConfigDrawer = ({ open, payer, onClose, onSaved }) => {
  const [config, setConfig] = useState(emptyConfig);
  // 'loading' | 'ready' | 'error'. Save is only possible once the stored config is in the form,
  // so an empty form never overwrites what is stored.
  const [loadState, setLoadState] = useState('loading');
  const [saving, setSaving] = useState(false);
  const update = (patch) => setConfig((prev) => ({ ...prev, ...patch }));
  const quillRef = useRef(null);
  const subjectRef = useRef(null);
  const lastFocus = useRef('body');

  // Inserts at the cursor of the subject or message, whichever was used last.
  const insertPlaceholder = (text) => {
    if (lastFocus.current === 'subject' && subjectRef.current) {
      const input = subjectRef.current;
      const start = input.selectionStart ?? input.value.length;
      const end = input.selectionEnd ?? start;
      const next = input.value.slice(0, start) + text + input.value.slice(end);
      update({ subject: next.slice(0, 500) });
      requestAnimationFrame(() => { input.focus(); input.setSelectionRange(start + text.length, start + text.length); });
      return;
    }
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const range = editor.getSelection(true);
    const index = range ? range.index : Math.max(editor.getLength() - 1, 0);
    editor.insertText(index, text, 'user');
    editor.setSelection(index + text.length, 0, 'user');
  };
  const healthPlanId = payer?.health_plan_id;

  useEffect(() => {
    if (!open || !healthPlanId) return;
    let cancelled = false;
    setConfig(emptyConfig);
    setLoadState('loading');
    fetchPayerEmailConfiguration(healthPlanId)
      .then((res) => {
        if (cancelled) return;
        setConfig(fromApi(res?.response_data));
        setLoadState('ready');
      })
      .catch(() => !cancelled && setLoadState('error'));
    return () => { cancelled = true; };
  }, [open, healthPlanId]);

  const handleSave = async () => {
    const errors = validate(config);
    if (errors.length) {
      toast.error(errors.join('\n'));
      return;
    }
    setSaving(true);
    try {
      const res = await updatePayerEmailConfiguration(healthPlanId, toApi(config));
      toast.success(res?.user_message || 'Email configuration saved successfully');
      onSaved?.(fromApi(res?.response_data));
      onClose();
    } catch (error) {
      // 4xx/5xx messages are already shown by the API client interceptor.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={saving ? undefined : onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, display: 'flex', flexDirection: 'column' } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid #e5e7eb' }}>
        <Typography sx={{ fontWeight: 600, fontSize: 15, color: '#344049' }}>
          {payer?.payerName}{payer?.healthplan_code ? ` - ${payer.healthplan_code}` : ''} | Email Configuration
        </Typography>
        <IconButton size="small" onClick={onClose} disabled={saving} aria-label="Close">
          <CloseIcon sx={{ fontSize: 20, color: '#969696' }} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 1 }}>
        {loadState === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
        )}
        {loadState === 'error' && (
          <Typography sx={{ py: 6, textAlign: 'center', fontSize: 13, color: '#c62828' }}>
            The email configuration could not be loaded. Close the panel and try again.
          </Typography>
        )}
        {loadState === 'ready' && (
          <>
            <Box sx={rowSx}>
              <Typography sx={{ ...labelSx, width: 'auto', mr: 2 }}>Send Email</Typography>
              <Switch size="small" checked={config.sendEmail} onChange={(e) => update({ sendEmail: e.target.checked })} />
            </Box>

            <Box sx={rowSx}>
              <Typography sx={labelSx}>From:</Typography>
              <TextField
                fullWidth
                size="small"
                value={config.fromAddress}
                placeholder="Set the From address under Email Configuration"
                disabled
                InputProps={{ sx: { fontSize: 13, backgroundColor: '#f1f2f4' } }}
                sx={{ '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
              />
            </Box>
            <Divider />

            <Box sx={rowSx}>
              <Typography sx={labelSx}>To:</Typography>
              <EmailChipsField value={config.toAddresses} onChange={(toAddresses) => update({ toAddresses })} placeholder="Enter recipient email addresses" />
            </Box>
            <Divider />

            <Box sx={rowSx}>
              <Typography sx={labelSx}>Cc:</Typography>
              <EmailChipsField value={config.ccAddresses} onChange={(ccAddresses) => update({ ccAddresses })} placeholder="Optional" />
            </Box>
            <Divider />

            <Box sx={rowSx}>
              <Typography sx={labelSx}>Subject:</Typography>
              <TextField
                fullWidth
                variant="standard"
                value={config.subject}
                onChange={(e) => update({ subject: e.target.value })}
                onFocus={() => { lastFocus.current = 'subject'; }}
                inputRef={subjectRef}
                placeholder="Enter notification subject line...."
                inputProps={{ maxLength: 500 }}
                InputProps={{ disableUnderline: true, sx: { fontSize: 13, '& input::placeholder': { fontStyle: 'italic' } } }}
              />
            </Box>

            <Box sx={{ mt: 1, '& .ql-container': { height: 200, fontSize: 13 }, '& .ql-toolbar, & .ql-container': { borderColor: '#d9dce1' }, '& .ql-toolbar': { borderRadius: '6px 6px 0 0' }, '& .ql-container.ql-snow': { borderRadius: '0 0 6px 6px' } }}>
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={config.body}
                onChange={(content) => update({ body: content === '<p><br></p>' ? '' : content })}
                onFocus={() => { lastFocus.current = 'body'; }}
                modules={editorModules}
                formats={editorFormats}
                placeholder="Type your notification message here..."
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
              <Typography sx={{ fontSize: 12, color: '#6b7684', mr: 0.5 }}>Insert:</Typography>
              {PLACEHOLDERS.map((p) => (
                <Chip
                  key={p}
                  label={p}
                  size="small"
                  clickable
                  // Keep the cursor where it is in the subject / message while clicking.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => insertPlaceholder(p)}
                  sx={{ fontSize: 11, color: '#1E88E5', backgroundColor: '#EAF4FD' }}
                />
              ))}
              <Typography sx={{ fontSize: 11, color: '#969696', width: '100%', mt: 0.25 }}>
                Placeholders are replaced with the payer, cycle, approver and organization when the roster is approved.
              </Typography>
            </Box>

            <FormControlLabel
              sx={{ mt: 2, '& .MuiFormControlLabel-label': { fontSize: 13, color: '#344049' } }}
              control={<Checkbox size="small" checked={config.includeRosterAttachment} onChange={(e) => update({ includeRosterAttachment: e.target.checked })} />}
              label="Include Roster File Attachment"
            />
          </>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, px: 3, py: 2, borderTop: '1px solid #e5e7eb' }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={saving}
          sx={{ textTransform: 'none', color: '#344049', borderColor: '#c4c4c4', '&:hover': { borderColor: '#969696', backgroundColor: '#fff' } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loadState !== 'ready' || saving}
          sx={{ textTransform: 'none', backgroundColor: '#FF7E07', boxShadow: 'none', '&:hover': { backgroundColor: '#E66F06', boxShadow: 'none' } }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Box>
    </Drawer>
  );
};

export default PayerEmailConfigDrawer;
